/**
 * Slide-over drawer with full user detail + role grants + recent audit/denial
 * activity. Dispatches PATCH/disable/enable/delete and onboarding-email
 * mutations.
 */
import { useMemo, useState } from "react";

import {
  adminApi,
  type AdminRole,
  type OnboardingEmailDraft,
} from "../../api/admin";
import { Drawer } from "../../components/admin/Drawer";
import { JsonExpand } from "../../components/admin/JsonExpand";
import { useConfirm } from "../../components/confirm/context";
import { useToast } from "../../components/toast/context";
import { EditableField } from "../../components/EditableField";
import { Pill } from "../../components/Pill";
import { QueryBoundary } from "../../components/QueryBoundary";
import { Skeleton } from "../../components/Skeleton";
import { formatRelative } from "../../lib/formatRelative";

interface Props {
  userId: string | null;
  onClose: () => void;
}

export function UserDetailDrawer({ userId, onClose }: Props) {
  const open = !!userId;
  return (
    <Drawer
      open={open}
      onClose={onClose}
      title="User detail"
      eyebrow="Admin"
      widthClass="w-[680px] max-w-full"
    >
      {userId ? <DrawerBody userId={userId} onClose={onClose} /> : null}
    </Drawer>
  );
}

function DrawerBody({ userId, onClose }: { userId: string; onClose: () => void }) {
  const userQuery = adminApi.users.get(userId);
  const rolesQuery = adminApi.roles.list();
  const auditQuery = adminApi.audit.query({ limit: 50 });

  const patchMutation = adminApi.users.patch(userId);
  const disableMutation = adminApi.users.disable(userId);
  const enableMutation = adminApi.users.enable(userId);
  const resyncSlackMutation = adminApi.users.resyncSlack(userId);
  const deleteMutation = adminApi.users.delete();
  const onboardingMutation = adminApi.users.onboardingEmail(userId);
  const grantRoleMutation = adminApi.users.grantRole(userId);
  const revokeRoleMutation = adminApi.users.revokeRole(userId);

  const toast = useToast();
  const confirm = useConfirm();
  const [emailDraft, setEmailDraft] = useState<OnboardingEmailDraft | null>(null);

  return (
    <>
      <QueryBoundary
        query={userQuery}
        loading={
          <div className="space-y-3">
            <Skeleton height={28} width="60%" />
            <Skeleton height={20} width="40%" />
            <Skeleton height={120} width="100%" />
          </div>
        }
      >
      {(user) => {
        const allRoles = rolesQuery.data ?? [];
        const grantedRoleIds = new Set(
          allRoles
            .filter((r) => user.role_names.includes(r.name))
            .map((r) => r.id),
        );
        const availableRoles = allRoles.filter(
          (r) => !grantedRoleIds.has(r.id),
        );

        const userAudit =
          auditQuery.data?.filter((a) => a.user_email === user.email) ?? [];

        return (
          <>
            <div className="flex items-center justify-between flex-wrap gap-3 mb-5">
              <div className="min-w-0">
                <div className="font-heading text-xl font-semibold text-ink truncate">
                  {user.email}
                </div>
                <div className="text-sm text-ink-low mt-1">
                  Created {formatRelative(user.created_at)}
                </div>
              </div>
              <div className="flex items-center gap-2">
                {user.disabled_at ? <Pill value="dismissed" /> : <Pill value="active" />}
                {user.disabled_at ? (
                  <button
                    type="button"
                    className="btn btn-second btn-sm"
                    disabled={enableMutation.isPending}
                    onClick={() => {
                      enableMutation.mutate(undefined, {
                        onSuccess: () => toast.ok("User enabled"),
                        onError: (e) => toast.error(e),
                      });
                    }}
                  >
                    Enable
                  </button>
                ) : (
                  <button
                    type="button"
                    className="btn btn-ghost btn-sm"
                    disabled={disableMutation.isPending}
                    onClick={() => {
                      disableMutation.mutate(undefined, {
                        onSuccess: () => toast.ok("User disabled"),
                        onError: (e) => toast.error(e),
                      });
                    }}
                  >
                    Disable
                  </button>
                )}
                <button
                  type="button"
                  className="btn btn-ghost btn-sm btn-danger"
                  disabled={deleteMutation.isPending}
                  onClick={async () => {
                    const ok = await confirm({
                      title: `Delete ${user.email}?`,
                      body: "This is permanent — all role grants and Slack mapping are removed.",
                      confirmLabel: "Delete user",
                      tone: "danger",
                    });
                    if (!ok) return;
                    deleteMutation.mutate(user.id, {
                      onSuccess: () => {
                        toast.ok("User deleted");
                        onClose();
                      },
                      onError: (e) => toast.error(e),
                    });
                  }}
                >
                  Delete
                </button>
              </div>
            </div>

            <section className="panel panel--flush mb-5">
              <div className="panel-header">
                <h3>Profile</h3>
              </div>
              <div className="panel-body grid gap-4" style={{ gridTemplateColumns: "minmax(120px, 0.4fr) 1fr" }}>
                <FieldLabel>Name</FieldLabel>
                <EditableField
                  value={user.name ?? ""}
                  label="Name"
                  placeholder="Display name"
                  onSave={(next) =>
                    patchMutation.mutateAsync({ name: next || null })
                  }
                />
                <FieldLabel>Slack user ID</FieldLabel>
                <div className="flex items-center gap-2">
                  <div className="flex-1">
                    <EditableField
                      value={user.slack_user_id ?? ""}
                      label="Slack user ID"
                      placeholder="U01234ABCD"
                      onSave={(next) =>
                        patchMutation.mutateAsync({
                          slack_user_id: next || null,
                        })
                      }
                    />
                  </div>
                  <button
                    type="button"
                    className="btn btn--secondary btn--sm"
                    onClick={() => resyncSlackMutation.mutate()}
                    disabled={resyncSlackMutation.isPending}
                    title="Re-run Slack users.lookupByEmail"
                  >
                    {resyncSlackMutation.isPending ? "…" : "Re-resolve"}
                  </button>
                </div>
                <FieldLabel>Default Slack channel</FieldLabel>
                <EditableField
                  value={user.default_slack_channel ?? ""}
                  label="Default Slack channel"
                  placeholder="#jarvis-firstname"
                  onSave={(next) =>
                    patchMutation.mutateAsync({
                      default_slack_channel: next || null,
                    })
                  }
                />
                <FieldLabel>Cron jobs</FieldLabel>
                <div className="flex items-center gap-3">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={user.cron_enabled}
                      onChange={(e) =>
                        patchMutation.mutate({
                          cron_enabled: e.target.checked,
                        })
                      }
                    />
                    <span className="text-sm">Enabled</span>
                  </label>
                  {patchMutation.isPending ? (
                    <span className="text-xs text-ink-low">saving…</span>
                  ) : null}
                </div>
              </div>
            </section>

            <section className="panel panel--flush mb-5">
              <div className="panel-header">
                <h3>Roles</h3>
                <span className="text-xs text-ink-low">
                  {user.role_names.length} granted
                </span>
              </div>
              <div className="panel-body">
                <div className="flex flex-wrap gap-2 mb-3">
                  {user.role_names.length === 0 ? (
                    <span className="text-sm text-ink-low">No roles granted.</span>
                  ) : (
                    user.role_names.map((roleName) => {
                      const role = allRoles.find((r) => r.name === roleName);
                      return (
                        <span key={roleName} className="tag-chip">
                          {roleName}
                          {role ? (
                            <button
                              type="button"
                              aria-label={`Revoke ${roleName}`}
                              onClick={() => {
                                revokeRoleMutation.mutate(role.id, {
                                  onSuccess: () =>
                                    toast.ok(`Revoked ${roleName}`),
                                  onError: (e) =>
                                    toast.error(e),
                                });
                              }}
                            >
                              ×
                            </button>
                          ) : null}
                        </span>
                      );
                    })
                  )}
                </div>
                <RolePicker
                  available={availableRoles}
                  onPick={(roleId) =>
                    grantRoleMutation.mutate(roleId, {
                      onSuccess: () => toast.ok("Role granted"),
                      onError: (e) => toast.error(e),
                    })
                  }
                  busy={grantRoleMutation.isPending}
                />
              </div>
            </section>

            <section className="panel panel--flush mb-5">
              <div className="panel-header">
                <h3>Onboarding</h3>
              </div>
              <div className="panel-body">
                <p className="text-sm text-ink-low mb-3">
                  Generate an onboarding email with the user's Microsoft sign-in
                  link, role summary, and Slack mapping. The draft is generated
                  server-side; copy it into your mail client.
                </p>
                <button
                  type="button"
                  className="btn btn-second btn-sm"
                  disabled={onboardingMutation.isPending}
                  onClick={() => {
                    onboardingMutation.mutate(undefined, {
                      onSuccess: (draft) => {
                        setEmailDraft(draft);
                        toast.ok("Draft generated");
                      },
                      onError: (e) => toast.error(e),
                    });
                  }}
                >
                  Generate onboarding email
                </button>
                {emailDraft ? (
                  <OnboardingPreview
                    draft={emailDraft}
                    onCopy={(what) => toast.ok(`${what} copied`)}
                  />
                ) : null}
              </div>
            </section>

            <section className="panel panel--flush mb-5">
              <div className="panel-header">
                <h3>Recent tool calls</h3>
                <span className="text-xs text-ink-low">
                  last {userAudit.length}
                </span>
              </div>
              <div className="panel-body">
                {userAudit.length === 0 ? (
                  <span className="text-sm text-ink-low">
                    No tool calls in the last batch.
                  </span>
                ) : (
                  <ul className="space-y-2">
                    {userAudit.map((entry) => (
                      <li
                        key={entry.id}
                        className="flex items-start justify-between gap-3 pb-2 border-b border-rule last:border-0"
                      >
                        <div className="min-w-0">
                          <div className="font-heading text-sm font-medium text-ink">
                            {entry.tool_name}
                          </div>
                          <div className="text-xs text-ink-low mt-0.5">
                            {formatRelative(entry.created_at)} ·{" "}
                            <span className="tnum">{entry.result_code}</span>
                          </div>
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </section>

            <DenialsForUser email={user.email} />
          </>
        );
      }}
      </QueryBoundary>
    </>
  );
}

function FieldLabel({ children }: { children: React.ReactNode }) {
  return (
    <span className="text-xs font-heading font-semibold text-ink-low pt-2">
      {children}
    </span>
  );
}

function RolePicker({
  available,
  onPick,
  busy,
}: {
  available: AdminRole[];
  onPick: (roleId: string) => void;
  busy: boolean;
}) {
  const [selected, setSelected] = useState("");
  if (available.length === 0) {
    return (
      <span className="text-xs text-ink-low">All available roles granted.</span>
    );
  }
  return (
    <div className="flex items-center gap-2">
      <select
        className="input"
        style={{ maxWidth: 280 }}
        value={selected}
        onChange={(e) => setSelected(e.target.value)}
      >
        <option value="">+ add role…</option>
        {available.map((role) => (
          <option key={role.id} value={role.id}>
            {role.name} ({role.workspace_slug})
          </option>
        ))}
      </select>
      <button
        type="button"
        className="btn btn-second btn-sm"
        disabled={!selected || busy}
        onClick={() => {
          if (!selected) return;
          onPick(selected);
          setSelected("");
        }}
      >
        Add
      </button>
    </div>
  );
}

function OnboardingPreview({
  draft,
  onCopy,
}: {
  draft: OnboardingEmailDraft;
  onCopy: (what: string) => void;
}) {
  const handleCopy = (text: string, label: string) => {
    void navigator.clipboard.writeText(text).then(() => onCopy(label));
  };
  return (
    <div className="mt-4 panel" style={{ padding: "1rem" }}>
      <div className="flex items-center justify-between mb-2">
        <div className="text-xs font-heading font-semibold text-ink-low">
          Subject
        </div>
        <button
          type="button"
          className="btn btn-ghost btn-sm"
          onClick={() => handleCopy(draft.subject, "Subject")}
        >
          Copy
        </button>
      </div>
      <div className="text-sm font-medium text-ink mb-3">{draft.subject}</div>
      <div className="flex items-center justify-between mb-2">
        <div className="text-xs font-heading font-semibold text-ink-low">
          Body
        </div>
        <button
          type="button"
          className="btn btn-ghost btn-sm"
          onClick={() => handleCopy(draft.body, "Body")}
        >
          Copy
        </button>
      </div>
      <pre className="args-json">{draft.body}</pre>
    </div>
  );
}

function DenialsForUser({ email }: { email: string }) {
  const denialsQuery = adminApi.denials.query({
    attempted_email: email,
    limit: 25,
  });
  const denials = useMemo(
    () => denialsQuery.data ?? [],
    [denialsQuery.data],
  );
  if (denials.length === 0) return null;
  return (
    <section className="panel panel--flush">
      <div className="panel-header">
        <h3>Recent denials</h3>
        <span className="text-xs text-ink-low">{denials.length} entries</span>
      </div>
      <div className="panel-body">
        <ul className="space-y-2">
          {denials.map((d) => (
            <li
              key={d.id}
              className="flex items-start justify-between gap-3 pb-2 border-b border-rule last:border-0"
            >
              <div className="min-w-0">
                <div className="text-sm font-medium text-ink">{d.reason}</div>
                <div className="text-xs text-ink-low mt-0.5">
                  {formatRelative(d.created_at)} ·{" "}
                  {d.source_ip || "—"}
                </div>
              </div>
              <JsonExpand value={d} preview="details" />
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}
